/**
 * OpenTelemetry Web SDK setup for shopdotapp-storefront.
 *
 * Fail-safe and ENV-driven.
 */

import { trace } from "@opentelemetry/api";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";

const SERVICE_NAME = "shopdotapp-storefront";

let initialized = false;

export function initTelemetry() {
  if (initialized) return;
  initialized = true;

  try {
    if (process.env.REACT_APP_OTEL_ENABLED === "false") return;

    const endpoint = process.env.REACT_APP_OTEL_ENDPOINT;

    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      "deployment.environment": process.env.REACT_APP_OTEL_ENVIRONMENT || process.env.NODE_ENV,
      "service.version": process.env.REACT_APP_VERSION || "unknown",
    });

    const provider = new WebTracerProvider({ resource });

    if (endpoint) {
      const exporter = new OTLPTraceExporter({ url: `${endpoint}/v1/traces` });
      provider.addSpanProcessor(new BatchSpanProcessor(exporter, {
        maxQueueSize: 100,
        scheduledDelayMillis: 5000,
      }));
    }

    provider.register({
      contextManager: new ZoneContextManager(),
    });

    const apiUrlPattern = /\/(api|v1)\//;

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [apiUrlPattern],
          clearTimingResources: true,
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: [apiUrlPattern],
        }),
      ],
    });
  } catch {
    // Fail-safe
  }
}

export function getTracer() {
  return trace.getTracer(SERVICE_NAME);
}

export function trackEvent(eventName, attributes = {}) {
  try {
    const tracer = trace.getTracer("product-events");
    const span = tracer.startSpan(eventName);
    span.setAttribute("event.domain", "product");
    span.setAttribute("event.name", eventName);
    Object.entries(attributes).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        span.setAttribute(key, String(value));
      }
    });
    span.end();
  } catch {
    // Fail-safe
  }
}

export function trackError(error, errorInfo = {}) {
  try {
    const tracer = trace.getTracer("product-events");
    const span = tracer.startSpan("frontend.error");
    span.setAttribute("error.type", error?.name || "Error");
    span.setAttribute("error.message", error?.message || String(error));
    span.setAttribute("page.url", window.location.pathname);
    span.recordException(error);
    span.end();
  } catch {
    // Fail-safe
  }
}
